import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, API, getToken, errMsg } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { toast } from "sonner";
import { Download, Mail, FileSpreadsheet, Brain, Loader2, Send, MessageSquare, Swords, Trash2 } from "lucide-react";

const STARTERS = [
  "What are my top 3 moves this month?",
  "Which SKUs should I stop advertising?",
  "Where should I grow next — which city?",
  "Why are my cancellations so high?",
];

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [params] = useSearchParams();
  const highlight = params.get("highlight");

  // AI chat state
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deletingUpload, setDeletingUpload] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get("/seller/reports").then((r) => setReports(r.data)).catch(() => {}),
      api.get("/seller/uploads").then((r) => setUploads(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (highlight) setSelected(highlight);
  }, [highlight]);

  useEffect(() => {
    if (!selected) {
      setSelectedDetail(null);
      setMessages([]);
      return;
    }
    api.get(`/seller/reports/${selected}`).then((r) => setSelectedDetail(r.data)).catch(() => {});
    setMessages([]);
  }, [selected]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, asking]);

  async function sendEmail(id) {
    setSending(id);
    try {
      const { data } = await api.post(`/seller/reports/${id}/email`);
      if (data.status === "sent") toast.success("Report emailed.");
      else if (data.status === "skipped") toast.warning("Email disabled (no RESEND key).");
      else toast.error(data.error || "Email failed");
    } catch (e) {
      toast.error(errMsg(e, "Email failed"));
    } finally {
      setSending(null);
    }
  }

  async function deleteReport(id) {
    if (!window.confirm("Delete this report permanently? The Excel file will be removed too.")) return;
    setDeleting(id);
    try {
      await api.delete(`/seller/reports/${id}`);
      setReports((prev) => prev.filter((r) => r.id !== id));
      if (selected === id) setSelected(null);
      toast.success("Report deleted");
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    } finally {
      setDeleting(null);
    }
  }

  async function deleteUpload(id) {
    if (!window.confirm("Delete this upload and all reports generated from it? This cannot be undone.")) return;
    setDeletingUpload(id);
    try {
      const { data } = await api.delete(`/seller/uploads/${id}`);
      setUploads((prev) => prev.filter((u) => u.id !== id));
      // Also drop any reports derived from this upload
      setReports((prev) => prev.filter((r) => r.upload_id !== id));
      if (selected && reports.find((r) => r.id === selected)?.upload_id === id) setSelected(null);
      toast.success(`Upload deleted · ${data.reports_deleted} linked reports removed`);
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    } finally {
      setDeletingUpload(null);
    }
  }

  async function ask(q) {
    const trimmed = (q ?? question).trim();
    if (!trimmed || !selected) return;
    setMessages((m) => [...m, { role: "user", text: trimmed }]);
    setQuestion("");
    setAsking(true);
    try {
      const { data } = await api.post("/seller/ai-chat", { report_id: selected, question: trimmed });
      setMessages((m) => [...m, { role: "ai", text: data.answer || "(no answer)" }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: errMsg(e, "Failed to answer") }]);
    } finally {
      setAsking(false);
    }
  }

  function downloadLink(id) {
    return `${API}/seller/reports/${id}/download?token=${getToken()}`;
  }

  return (
    <AppShell role="seller">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold">Reports</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">AI Growth Reports.</h1>
          <p className="text-white/60 text-sm mt-1">Every upload produces a 6-sheet Excel with executive narrative and ads plan.</p>
        </div>
      </div>

      {loading && <div className="text-white/60">Loading…</div>}
      {!loading && reports.length === 0 && uploads.length === 0 && (
        <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-8 text-white">
          <div className="font-display text-2xl font-black tracking-tighter">No reports yet.</div>
          <div className="text-sm text-slate-400 mt-1">Upload a CSV/XLSX first and the report will appear here.</div>
        </div>
      )}

      {!loading && uploads.length > 0 && (
        <div className="mb-6" data-testid="uploads-section">
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 font-bold mb-2">
            Uploaded files ({uploads.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {uploads.map((u) => (
              <div
                key={u.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10"
                data-testid={`upload-row-${u.id}`}
              >
                <FileSpreadsheet size={18} className="text-[#4F46E5] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.original_filename}</div>
                  <div className="text-[11px] text-white/50 font-mono-data">
                    {u.platform} · {(u.row_count || 0).toLocaleString()} rows ·{" "}
                    {new Date(u.created_at).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => deleteUpload(u.id)}
                  disabled={deletingUpload === u.id}
                  className="p-2 rounded-md hover:bg-[#4F46E5]/20 text-white/50 hover:text-[#4F46E5] transition-colors disabled:opacity-50"
                  data-testid={`delete-upload-btn-${u.id}`}
                  title="Delete upload (and all reports generated from it)"
                >
                  {deletingUpload === u.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-3">
          {reports.map((r) => (
            <div
              key={r.id}
              onClick={() => setSelected(r.id)}
              className={`bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-5 text-white cursor-pointer hover-lift ${
                selected === r.id ? "border-[#4F46E5]" : ""
              }`}
              data-testid={`report-card-${r.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#4F46E5] flex items-center justify-center">
                    <FileSpreadsheet size={18} className="text-white" />
                  </div>
                  <div>
                    <div className="font-display text-lg font-black tracking-tighter uppercase">{r.platform}</div>
                    <div className="text-[11px] text-slate-500 font-mono-data">
                      {new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={downloadLink(r.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="px-3 py-1.5 border border-white/[0.08] rounded-md text-xs font-bold hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors flex items-center gap-1.5"
                    data-testid={`download-btn-${r.id}`}
                    download
                  >
                    <Download size={12} /> Download
                  </a>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteReport(r.id);
                    }}
                    disabled={deleting === r.id}
                    className="px-2.5 py-1.5 border border-white/[0.08] rounded-md text-xs font-bold text-slate-500 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors flex items-center gap-1 disabled:opacity-60"
                    data-testid={`delete-report-btn-${r.id}`}
                    title="Delete report"
                  >
                    {deleting === r.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detail + AI chat pane */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white" data-testid="report-detail-pane">
            <div className="flex items-center gap-2 mb-3">
              <Brain size={16} className="text-[#4F46E5]" />
              <div className="text-sm font-bold uppercase tracking-[0.2em]">AI Narrative</div>
            </div>
            {!selectedDetail && (
              <div className="text-sm text-slate-500">Select a report to see the AI narrative.</div>
            )}
            {selectedDetail && (
              <div className="text-sm whitespace-pre-wrap text-slate-300 leading-relaxed max-h-[360px] overflow-auto" data-testid="ai-narrative-text">
                {selectedDetail.ai_narrative || "(No narrative)"}
              </div>
            )}
          </div>

          {/* AI Q&A */}
          {selectedDetail && (
            <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white" data-testid="ai-chat">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare size={16} className="text-[#4F46E5]" />
                <div className="text-sm font-bold uppercase tracking-[0.2em]">Ask your data</div>
              </div>
              <div className="text-xs text-slate-500 mb-3">
                Chat with Claude Sonnet 4.5 about this report.
              </div>
              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      onClick={() => ask(s)}
                      className="text-[11px] px-3 py-1.5 rounded-full border border-white/[0.08] hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors"
                      data-testid={`chat-starter-${s.slice(0, 20)}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <div className="space-y-2 max-h-[360px] overflow-auto mb-3 pr-1">
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`text-xs rounded-md p-3 whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#4F46E5] text-white ml-8"
                        : "bg-white/[0.02] text-slate-200 mr-8"
                    }`}
                  >
                    {m.text}
                  </div>
                ))}
                {asking && (
                  <div className="text-xs rounded-md p-3 bg-white/[0.02] text-slate-500 mr-8 flex items-center gap-2">
                    <Loader2 size={12} className="animate-spin" /> thinking…
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  ask();
                }}
                className="flex gap-2"
                data-testid="chat-form"
              >
                <input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask anything about this report…"
                  className="flex-1 px-3 py-2 text-sm border border-white/[0.08] rounded-md focus:outline-none focus:border-[#4F46E5]"
                  data-testid="chat-input"
                  disabled={asking}
                />
                <button
                  type="submit"
                  disabled={asking || !question.trim()}
                  className="btn-primary text-sm disabled:opacity-50"
                  data-testid="chat-send"
                >
                  <Send size={14} />
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
